<template>
  <div :class="bem()">
    <ul :class="bem('items-inner')">
      <template
        v-for="lineItem in lineItems"
        :key="lineItem?.bundleId"
      >
        <CartItem
          v-if="!lineItem?.bundleParentSku"
          :cart-item="lineItem"
          :lens-tech-upsell-skus="skusToPersistLensTechUpsellOn"
          @polarisation-upgrade="handleLensTechUpgrade"
        />
        <CartDiscountedItem
          v-else-if="lineItem?.bundleParentSku"
          :theme="lineItem.bundleUsesTheme || 'default'"
          :cart-item="lineItem"
        />
      </template>
      <template
        v-if="
          shopifyStore.optimisticCartCount >
            (shopifyStore.bundledCartItemsTotal || 0)
        "
      >
        <template
          v-for="i in shopifyStore.optimisticCartCount -
            (shopifyStore.bundledCartItemsTotal || 0)"
          :key="i"
        >
          <CartItemShimmer />
        </template>
      </template>
    </ul>

    <UpsellsBlock
      :class="bem('upsells-block')"
      :line-items="lineItems"
    />
  </div>
</template>

<script setup lang="ts">
import { getUpdateProductQuantityPayload } from '~/assets/js/helpers/shopify'

const bem = useBEM('CartSidebarItems')
const shopifyStore = useShopifyStore() as any
const { $track, $product } = useNuxtApp() as any

interface UpgradeDetail {
  oldSku: string
  newSku: string
}
const skusToPersistLensTechUpsellOn = ref<UpgradeDetail[]>([])
const customisedProducts = ref<any[]>([])

const lineItems = computed(() => {
  return Object.entries(shopifyStore.bundledCartItems)
    .map(([bundleId, bundle]: [any, any]) => {
      if (bundle.summary) {
        return {
          bundle,
          bundleItems: bundle.items,
          bundleId: bundleId,
          bundleSku: bundle.summary.bundleSku,
          bundleCost: bundle.summary.bundleCost,
          bundleOriginalCost: bundle.summary.bundleOriginalCost,
          bundleCostDifference: bundle.summary.bundleCostDifference,
          bundleBaseSku: bundle.summary.bundleBaseSku,
          bundleQuantity: bundle.summary.bundleQuantity,
          bundleDisplayOrder: bundle.summary.bundleDisplayOrder,
          bundleCheckoutInfo: bundle.summary.bundleCheckoutInfo,
          bundleDespatchDate: bundle.summary.bundleDespatchDate,
          bundleIsGiftCard: bundle.summary.bundleIsGiftCard,
          bundleParentSku: bundle.summary.bundleParentSku,
          bundleParentBaseSku: bundle.summary.bundleParentBaseSku,
          bundleUsesTheme: bundle.summary.bundleUsesTheme
        }
      }
    })
    .sort((a: any, b: any) => {
      return a.bundleDisplayOrder - b.bundleDisplayOrder
    })
})

async function trackCartViewed() {
  await getCustomisedProducts()
  const cart = shopifyStore.cart

  const cartDetails = {
    id: shopifyStore.cartId,
    itemCount: cart.totalQuantity,
    currencyCode: cart.cost.totalAmount?.currencyCode,
    giftCardOrCouponCode: cart.discountCodes[0]?.code || null,
    totalAmountWithTaxesFloat: cart.cost.totalAmount?.amount,
    subtotalAmountFloat: cart.cost.subtotalAmount?.amount,
    taxAmountFloat: cart.cost.totalTaxAmount?.amount || null,
    discountAmountFloat: shopifyStore.totalDiscount || null,
    lineItems: lineItems.value,
  }

  await $track.cart.view({
    order: cartDetails,
    customisedProducts: customisedProducts.value,
  })
}

async function getCustomisedProducts() {
  customisedProducts.value = Object.fromEntries(
    await Promise.all(
      lineItems.value.map(async (lineItem: any) => {
        const sku = lineItem.bundleSku
        return [
          lineItem.bundleId,
          await $product.getCustomisedProduct(lineItem.bundleBaseSku, { sku }),
        ]
      })
    )
  )
}

async function handleLensTechUpgrade({
  customisedProduct,
  updatedSku,
  newCartItems,
  previousSku,
  isDowngrade,
}: {
  customisedProduct: any
  updatedSku: string
  newCartItems: any
  previousSku: string
  isDowngrade: boolean
}) {
  if (isDowngrade) {
    skusToPersistLensTechUpsellOn.value =
      skusToPersistLensTechUpsellOn.value.filter((sku) => {
        return sku.newSku !== previousSku
      })
  } else {
    skusToPersistLensTechUpsellOn.value.push({
      newSku: updatedSku,
      oldSku: previousSku,
    })
  }

  const existingProductBundleInCart =
    shopifyStore.existingBundlesInCartFromSku(updatedSku)

  if (existingProductBundleInCart.length) {
    // Update new sku then remove current item instead of a straight swap
    const updatePayload = getUpdateProductQuantityPayload(
      existingProductBundleInCart,
      1
    )
    await shopifyStore.updateCartItems(updatePayload)
    await shopifyStore.removeCartItems([previousSku])
  } else {
    await shopifyStore.swapCartItems(newCartItems, [previousSku])
  }

  if (isDowngrade) {
    await $track.product.downgrade(customisedProduct)
  } else {
    await $track.product.upgrade(customisedProduct)
  }
}

onMounted(() => {
  if (shopifyStore.fetched) {
    trackCartViewed()
  }
})

// Catch if cart details get fetched after mount
watch(
  () => shopifyStore.fetched,
  async () => {
    if (shopifyStore.fetched) {
      trackCartViewed()
    }
  }
)
</script>
<style lang="postcss">
.CartSidebarItems {
  @apply flex flex-col justify-between gap-8
  pb-[var(--cart-inline-padding)] overflow-y-auto overflow-x-clip
  pt-3
  h-full;
}

.CartSidebarItems__upsells {
  @apply flex items-center justify-center w-full  min-h-[240px] bg-grey-100 rounded-lg;
}

.CartSidebarItems__items-inner {
  @apply relative list-none
  px-[var(--cart-inline-padding)]
  grid grid-cols-1 gap-4 content-start
  shrink-[2] grow;
}
</style>
